<template>
    <div class="mainfont page-container">
        <v-snackbar v-model="showSnackBar" color="#0f172a" timeout="4000">
            {{ msg }}
            <template v-slot:action="{ attrs }">
                <v-btn icon small v-bind="attrs" @click="showSnackBar = false">
                    <v-icon small color="white">mdi-close</v-icon>
                </v-btn>
            </template>
        </v-snackbar>
        <vue-element-loading :active="appLoading && !list.length" :is-full-screen="true" background-color="#FFFFFF"
            color="#13736f" spinner="spinner" />

        <PageHeader :title="campName ? 'Blocked Dates — ' + campName : 'Blocked Dates'"
            subtitle="Dates you have reserved for this camp. Applicants cannot book these dates."
            icon="mdi-calendar-lock-outline" :count="list.length">
            <template v-slot:actions>
                <v-btn depressed class="btn-act btn-act--neutral" style="height: 40px !important" @click="goBack">
                    <v-icon left small>mdi-arrow-left</v-icon>
                    Back
                </v-btn>
                <v-btn depressed class="btn-act btn-act--primary" style="height: 40px !important" @click="goToBlock">
                    <v-icon left small>mdi-plus</v-icon>
                    Block dates
                </v-btn>
            </template>
        </PageHeader>

        <section class="surface">
            <div v-if="list.length > 0" class="table-scroll">
                <v-simple-table class="app-table">
                    <thead>
                        <tr>
                            <th class="text-left">SL.No</th>
                            <th class="text-left">From</th>
                            <th class="text-left">To</th>
                            <th class="text-left">Status</th>
                            <th class="text-left">Remarks</th>
                            <th class="text-left">Blocked by</th>
                            <th class="text-left">Blocked on</th>
                            <th class="text-left">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, i) in list" :key="item._id">
                            <td data-label="#">{{ i + 1 }}</td>

                            <td data-label="From" class="text-no-wrap">
                                <div class="cell-strong">
                                    <v-icon small color="#13736f">mdi-calendar-blank-outline</v-icon>
                                    {{ formatDate(item.fromDate) }}
                                </div>
                            </td>

                            <td data-label="To" class="text-no-wrap">
                                <div class="cell-strong">
                                    <v-icon small color="#13736f">mdi-calendar-blank-outline</v-icon>
                                    {{ formatDate(item.toDate) }}
                                </div>
                                <div class="cell-muted">{{ dayCount(item) }}</div>
                            </td>

                            <td data-label="Status">
                                <span class="status-pill" :class="'status-pill--' + periodOf(item).tone">
                                    {{ periodOf(item).label }}
                                </span>
                            </td>

                            <td data-label="Remarks">
                                <div v-if="item.remarks" class="remarks-text">{{ item.remarks }}</div>
                                <div v-else class="cell-muted remarks-empty">No remarks</div>
                            </td>

                            <td data-label="Blocked by">
                                <div class="cell-strong text-capitalize">{{ item.blockedBy || "—" }}</div>
                            </td>

                            <td data-label="Blocked on" class="text-no-wrap">
                                <div class="cell-muted" style="margin-top: 0">{{ formatDate(item.created_at) }}</div>
                            </td>

                            <td data-label="Actions" class="app-table-actions">
                                <div class="row-actions">
                                    <v-btn depressed small class="btn-act btn-act--sm btn-act--info"
                                        @click="editDate(item)">
                                        <v-icon left x-small>mdi-pencil-outline</v-icon>
                                        Edit
                                    </v-btn>
                                    <v-btn depressed small class="btn-act btn-act--sm btn-act--danger"
                                        @click="deleteDate(item)">
                                        <v-icon left x-small>mdi-delete-outline</v-icon>
                                        Delete
                                    </v-btn>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </div>

            <div v-else-if="!appLoading" class="empty-state">
                <v-icon size="56">mdi-calendar-check-outline</v-icon>
                <div class="cell-strong">No blocked dates</div>
                <div class="cell-muted">Dates you block for this camp will be listed here.</div>
                <v-btn depressed class="btn-act btn-act--primary mt-3" @click="goToBlock">
                    <v-icon left small>mdi-plus</v-icon>
                    Block dates
                </v-btn>
            </div>
        </section>

        <!-- Edit -->
        <v-dialog v-model="editDialog" max-width="520px" persistent>
            <v-card class="dialog-card">
                <div class="dialog-head">
                    <div>
                        <div class="dialog-head-title">Edit blocked dates</div>
                        <div class="dialog-head-subtitle">Change the blocked period or the remarks.</div>
                    </div>
                </div>
                <v-card-text class="dlg-body">
                    <v-row dense>
                        <v-col cols="12" sm="6">
                            <label class="dlg-label">From date</label>
                            <DateField v-model="editedFromDate" outlined dense hide-details="auto"
                                background-color="white" :min="todayIso" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <label class="dlg-label">To date</label>
                            <DateField v-model="editedToDate" outlined dense hide-details="auto"
                                background-color="white" :min="editedFromDate || todayIso" />
                        </v-col>
                        <v-col cols="12">
                            <label class="dlg-label">Remarks</label>
                            <v-textarea v-model="editedRemarks" outlined dense rows="3" auto-grow counter="250"
                                maxlength="250" color="#13736f" background-color="white"
                                placeholder="Reason for blocking these dates" />
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions class="px-5 pb-5">
                    <v-spacer />
                    <v-btn depressed class="btn-act btn-act--neutral" @click="cancelEdit">Cancel</v-btn>
                    <v-btn depressed class="btn-act btn-act--primary" :loading="saving"
                        :disabled="!editedFromDate || !editedToDate" @click="saveEditedDate(itemid)">
                        <v-icon left small>mdi-content-save-outline</v-icon>
                        Save changes
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Delete -->
        <v-dialog v-model="deleteDialog" max-width="460px" persistent>
            <v-card class="dialog-card">
                <div class="dialog-head dialog-head--danger">
                    <div>
                        <div class="dialog-head-title">Remove blocked dates</div>
                        <div class="dialog-head-subtitle">These dates will open up for booking again.</div>
                    </div>
                </div>
                <v-card-text class="dlg-body" v-if="itemToDelete">
                    <p class="dlg-text">Are you sure you want to remove this block?</p>
                    <div class="dlg-summary">
                        <div class="cell-strong">
                            <v-icon small color="#b91c1c">mdi-calendar-remove-outline</v-icon>
                            {{ formatDate(itemToDelete.fromDate) }} – {{ formatDate(itemToDelete.toDate) }}
                        </div>
                        <div v-if="itemToDelete.remarks" class="cell-muted">{{ itemToDelete.remarks }}</div>
                    </div>
                </v-card-text>
                <v-card-actions class="px-5 pb-5">
                    <v-spacer />
                    <v-btn depressed class="btn-act btn-act--neutral" @click="cancelDelete">Cancel</v-btn>
                    <v-btn depressed class="btn-act btn-act--danger-solid" :loading="saving" @click="confirmDelete">
                        <v-icon left small>mdi-delete-outline</v-icon>
                        Remove
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import PageHeader from "@/components/Common/PageHeader";

export default {
    components: { PageHeader },
    data() {
        return {
            id: this.$route.query.id,
            list: [],
            campName: "",
            appLoading: false,
            saving: false,
            msg: null,
            showSnackBar: false,

            deleteDialog: false,
            itemToDelete: null,

            editDialog: false,
            itemid: null,
            editedFromDate: null,
            editedToDate: null,
            editedRemarks: "",
        };
    },

    mounted() {
        this.getList();
    },

    computed: {
        // Earliest date the edit dialog accepts: blocks cannot be moved into the past.
        todayIso() {
            return moment().format("YYYY-MM-DD");
        },
    },

    methods: {
        notify(text) {
            this.msg = text;
            this.showSnackBar = true;
        },
        goBack() {
            this.$router.go(-1);
        },
        goToBlock() {
            this.$router.push({ path: "/blockdate", query: { id: this.id } });
        },

        // Blocked dates are stored as midnight UTC, so format in UTC to keep the day.
        formatDate(value) {
            return value ? moment.utc(value).format("DD MMM YYYY") : "—";
        },
        dayCount(item) {
            const days = moment.utc(item.toDate).diff(moment.utc(item.fromDate), "days") + 1;
            return days === 1 ? "1 day" : `${days} days`;
        },
        periodOf(item) {
            const today = moment().format("YYYY-MM-DD");
            const to = moment.utc(item.toDate).format("YYYY-MM-DD");
            // Only two states: the block is still in force (today or later) or it has ended.
            if (to < today) return { label: "Past", tone: "neutral" };
            return { label: "Active", tone: "success" };
        },

        getList() {
            this.appLoading = true;
            axios({
                method: "POST",
                url: "/list/block",
                headers: { token: localStorage.getItem("token") },
                data: { campid: this.id },
            })
                .then((response) => {
                    this.list = response.data.data || [];
                    this.campName = response.data.campName || "";
                })
                .catch((err) => {
                    console.log(err);
                    this.notify("Could not load the blocked dates. Please try again.");
                })
                .finally(() => {
                    this.appLoading = false;
                });
        },

        // ---- edit ----
        editDate(item) {
            this.itemid = item._id;
            this.editedFromDate = moment.utc(item.fromDate).format("YYYY-MM-DD");
            this.editedToDate = moment.utc(item.toDate).format("YYYY-MM-DD");
            this.editedRemarks = item.remarks || "";
            this.editDialog = true;
        },
        cancelEdit() {
            this.editedFromDate = null;
            this.editedToDate = null;
            this.editedRemarks = "";
            this.itemid = null;
            this.editDialog = false;
        },
        saveEditedDate(itemid) {
            if (!this.editedFromDate || !this.editedToDate) return;
            this.saving = true;
            axios({
                method: "POST",
                url: "/divsion/edit/blockdates",
                headers: { token: localStorage.getItem("token") },
                data: {
                    blockid: itemid,
                    fromDate: this.editedFromDate,
                    toDate: this.editedToDate,
                    remarks: this.editedRemarks,
                    status: "edit",
                },
            })
                .then((response) => {
                    this.notify(response.data.msg);
                    if (response.data.status) {
                        this.cancelEdit();
                        this.getList();
                    }
                })
                .catch((error) => {
                    console.error("Error updating dates:", error);
                    this.notify("Error updating dates. Please try again.");
                })
                .finally(() => {
                    this.saving = false;
                });
        },

        // ---- delete ----
        deleteDate(item) {
            this.itemToDelete = item;
            this.deleteDialog = true;
        },
        cancelDelete() {
            this.itemToDelete = null;
            this.deleteDialog = false;
        },
        confirmDelete() {
            if (!this.itemToDelete) return;
            this.saving = true;
            axios({
                method: "POST",
                url: "/divsion/edit/blockdates",
                headers: { token: localStorage.getItem("token") },
                data: { blockid: this.itemToDelete._id, status: "delete" },
            })
                .then((response) => {
                    this.notify(response.data.msg);
                    if (response.data.status) {
                        this.cancelDelete();
                        this.getList();
                    }
                })
                .catch((error) => {
                    console.error("Error deleting date:", error);
                    this.notify("Error deleting date. Please try again.");
                })
                .finally(() => {
                    this.saving = false;
                });
        },
    },
};
</script>

<style scoped>
.remarks-text {
    max-width: 320px;
    font-size: 14px;
    line-height: 1.45;
    color: #1e293b;
    white-space: pre-wrap;
    word-break: break-word;
}

.remarks-empty {
    font-style: italic;
}

.dlg-body {
    padding-top: 20px !important;
}

.dlg-label {
    display: block;
    margin-bottom: 6px;
    font-size: 12.5px;
    font-weight: 700;
    color: #334155;
}

.dlg-text {
    margin: 0 0 12px;
    font-size: 14px;
    color: #475569;
}

.dlg-summary {
    padding: 12px 14px;
    border-radius: 12px;
    background: #fef2f2;
    border: 1px solid #fecaca;
}

@media (max-width: 768px) {
    .remarks-text {
        max-width: none;
    }
}
</style>
